import React from "react";
import { Platform } from "react-native";
import * as Haptics from "expo-haptics";
import LScreen from "@components/LScreen";
import LBox from "@components/LBox";
import LText from "@components/LText";
import LPressable from "@components/LPressable";
import { fonts, theme } from "@theme";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { captureException } from "@utils/captureException";
import { router } from "expo-router";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import {
  runOnJS,
  useSharedValue,
  withDelay,
  withTiming,
} from "react-native-reanimated";
import {
  getOnboardingSwipeAction,
  type OnboardingSwipeAction,
} from "./onboardingSwipe";

const SWIPE_LOCK_MS = 450;

const triggerIntroHaptic = () => {
  if (Platform.OS === "web") return;

  Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch((error) => {
    captureException(error, {
      channel: "ignore",
      area: "haptics",
      operation: "intro-impact",
    });
  });
};

const StepIndicator = ({
  totalSteps,
  currentStep,
}: {
  totalSteps: number;
  currentStep: number;
}) => (
  <LBox
    flexDirection="row"
    justifyContent="center"
    alignItems="center"
    gap="s2"
    mb="l1"
  >
    {Array.from({ length: totalSteps }).map((_, i) => (
      <LBox
        key={i}
        height={4}
        width={60}
        borderRadius="max"
        backgroundColor={i < currentStep ? "white" : "greyBorder"}
      />
    ))}
  </LBox>
);

type IntroLayoutProps = {
  /** Total number of steps in this group (2 for quiz, 4 for aha) */
  totalSteps: number;
  /** Current step (1-indexed) */
  currentStep: number;
  /** Content rendered between stepper and button */
  children: React.ReactNode;
  /** Button label */
  buttonLabel?: string;
  /** Button press handler. Omit to hide the button. */
  onButtonPress?: () => void;
  /** Button sentry event name */
  buttonEventName: string;
  /** Button disabled state */
  buttonDisabled?: boolean;
  /** Hide the step indicator */
  hideSteps?: boolean;
  /** Override the default router back action for a swipe right. */
  onSwipeBack?: () => void;
};

const IntroLayout = ({
  totalSteps,
  currentStep,
  children,
  buttonLabel = "Continue",
  onButtonPress,
  buttonEventName,
  buttonDisabled = false,
  hideSteps = false,
  onSwipeBack,
}: IntroLayoutProps) => {
  const insets = useSafeAreaInsets();
  const swipeLocked = useSharedValue(0);
  const canSwipeNext = Boolean(onButtonPress) && !buttonDisabled;

  const handleSwipeNavigation = React.useCallback(
    (action: OnboardingSwipeAction) => {
      if (action === "next" && (!onButtonPress || buttonDisabled)) return;

      triggerIntroHaptic();

      if (action === "next") {
        onButtonPress?.();
      } else if (onSwipeBack) {
        onSwipeBack();
      } else {
        router.back();
      }
    },
    [buttonDisabled, onButtonPress, onSwipeBack],
  );

  const swipeGesture = Gesture.Pan()
    .withTestId("onboarding-swipe")
    .enabled(Platform.OS !== "web")
    .activeOffsetX([-24, 24])
    .failOffsetY([-18, 18])
    .onEnd((event) => {
      "worklet";
      if (swipeLocked.value !== 0) return;

      const action = getOnboardingSwipeAction(event);
      if (!action || (action === "next" && !canSwipeNext)) return;

      swipeLocked.value = 1;
      swipeLocked.value = withDelay(
        SWIPE_LOCK_MS,
        withTiming(0, { duration: 0 }),
      );
      runOnJS(handleSwipeNavigation)(action);
    });

  return (
    <GestureDetector gesture={swipeGesture}>
      <LScreen>
        <LBox flex={1} px="s4" style={{ paddingTop: insets.top + 8 }}>
          {!hideSteps && (
            <LBox style={{ zIndex: 1 }}>
              <StepIndicator
                totalSteps={totalSteps}
                currentStep={currentStep}
              />
            </LBox>
          )}
          {children}
        </LBox>

        {onButtonPress && (
          <LBox px="s4" pb="l3">
            <LBox opacity={buttonDisabled ? 0.4 : 1}>
              <LPressable
                fadeEnabled
                sentryEventName={buttonEventName}
                onPress={() => {
                  triggerIntroHaptic();
                  onButtonPress();
                }}
                disabled={buttonDisabled}
                style={{
                  backgroundColor: theme.colors.white,
                  borderRadius: 9999,
                  paddingVertical: theme.spacing.s4,
                  paddingHorizontal: theme.spacing.s4,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LText
                  fontSize={16}
                  fontFamily={fonts.semiBold}
                  color="screenBg"
                >
                  {buttonLabel}
                </LText>
              </LPressable>
            </LBox>
          </LBox>
        )}
      </LScreen>
    </GestureDetector>
  );
};

export default IntroLayout;
